import gql from "graphql-tag";

import { OverviewAppSyncClient } from "../clients/OverviewAppSyncClient";
import {
    findBatchJobsByMonth,
    listFilingPacks,
    listFilingPackStatuses,
    filingPack,
    listDeclarationPeriodStatuses,
    listDeclarationPeriods,
    listDeclarationPeriodEvents,
    findSystemEventsByFilingPackId,
    getPayloadUrlOnly
} from "../../graphql/queries";
import { createAndProduceBatches, flushFilingPacks } from "../../graphql/mutations";
import {
    LocalClearanceBatch,
    MonthOption,
    Exception,
    FilingPackFilter,
    DeclarationPeriodFilter,
    DeclarationPeriodEventFilter
} from "../Types";
import { OverviewHttpClient } from "../clients/OverviewHttpClient";
import { DateFormatter } from "../DateFormatter";

export class OverviewService {

    constructor(
        private overviewAppSyncClient: OverviewAppSyncClient,
        private overviewHttpClient: OverviewHttpClient
    ) {}

    async findBatchJobsByMonth({month, year}: MonthOption): Promise<any> {
        return this.overviewAppSyncClient.appSyncClient.query({
            query: gql(findBatchJobsByMonth),
            fetchPolicy: 'network-only',
            variables: {month, year}
        })
    }

    async createAndProduceBatches(batches : LocalClearanceBatch[]): Promise<any> {
        return this.overviewAppSyncClient.appSyncClient.mutate({
            mutation: gql(createAndProduceBatches), 
            variables: {
                dates: batches.map(x => x.day_bucket_date)
            }
        })
    }

    async retryExceptions(exceptions: Exception[]): Promise<void> {
      return await this.overviewHttpClient.request<void>('/api/exception/retry', undefined, { ids: exceptions.filter(e => e.id).map(e => e.id)});
    }

    async flushFilingPacks(userId: string): Promise<any> {
        return this.overviewAppSyncClient.appSyncClient.mutate({
            mutation: gql(flushFilingPacks),
            variables: { userId }
        });
    }

    async listFilingPacks(filter?: FilingPackFilter): Promise<any> {
        return this.overviewAppSyncClient.appSyncClient.query({
            query: gql(listFilingPacks),
            fetchPolicy: 'network-only',
            variables: { filter }
        })
    }

    async listFilingPackStatuses(): Promise<any> {
        return this.overviewAppSyncClient.appSyncClient.query({
            query: gql(listFilingPackStatuses),
            fetchPolicy: 'network-only',
        })
    }

    async listDeclarationPeriodStatuses(): Promise<any> {
        return this.overviewAppSyncClient.appSyncClient.query({
            query: gql(listDeclarationPeriodStatuses),
            fetchPolicy: 'network-only',
        })
    }

    async listDeclarationPeriods(filter?: DeclarationPeriodFilter): Promise<any> {
        return this.overviewAppSyncClient.appSyncClient.query({
            query: gql(listDeclarationPeriods),
            fetchPolicy: 'network-only',
            variables: { filter }
        })
    }

    async listDeclarationPeriodEvents(filter: DeclarationPeriodEventFilter): Promise<any> {
        return this.overviewAppSyncClient.appSyncClient.query({
            query: gql(listDeclarationPeriodEvents),
            fetchPolicy: 'network-only',
            variables: { filter }
        })
    }

    async getFilingPack(filingPackId: string): Promise<any> {
        return this.overviewAppSyncClient.appSyncClient.query({
            query: gql(filingPack),
            fetchPolicy: 'network-only',
            variables: { filingPackId }
        })
    }

    async findSystemEventsByFilingPackId(filingPackId: string): Promise<any> {
        return this.overviewAppSyncClient.appSyncClient.query({
            query: gql(findSystemEventsByFilingPackId),
            fetchPolicy: 'network-only',
            variables: { filingPackId }
        })
    }

    async getPayloadUrl(region: string, bucket: string, key: string, version?: string): Promise<any> {
        const variables: Record<string, string> = { region, bucket, key };
        if (version) {
            variables.version = version;
        }
        return this.overviewAppSyncClient.appSyncClient.query({
            query: gql(getPayloadUrlOnly),
            fetchPolicy: 'network-only',
            variables
        })
    }

    async findExceptions(): Promise<Exception[]> {
        const exceptions = await this.overviewHttpClient.request<Exception[]>('/api/exception');

        return exceptions.map(exception => {
            const createdAt = DateFormatter.createFromISO(exception.createdAt)

            return {
                ...exception,
                momentOfLocalClearance: exception.momentOfLocalClearance?.substring(0, 10) || '',
                createdAt: DateFormatter.formatDate(createdAt, 'dd-MM-yyyy HH:mm'),
            }
        });
    }

}